import axios from 'axios'
import { jsonp } from 'vue-jsonp'
import GetCookie from '../GetCookie'
import Cookie from '../util/Cookie'
import PathUtil from '../util/PathUtil'
import { check } from './CheckResponseJson'
import RecommendResponse, { RecommendItem } from './RecommendResponse'

// ---------------------------------------------------------------
// 定数
const REQ_PATH_RECOMMEND = '/pycre5/jsonp/recommend'
const API_PATH_REQUEST_ID = '/smz_api/SERecommend/requestid'

const MERCH_ID = 'shimadzu'

const PAGES = {
  mypage: { application: 'MA', product: 'MP' },
  productpage: { application: 'PA', product: 'PP' }
}

enum ErrorCode {
  SENDERROR = 'send_error',
  JSONNULL = 'json_is_null',
  JSONFORMAT = 'json_format_error',
  NONEXISTPARAM = 'json_item_error_'
}

interface GtmPost {
  [index: string]: string | number
}

interface SpecId {
  product: string
  application: string
}

export type RecommendType = 'productpage' | 'mypage'

export type SpecType = 'product' | 'application'

/**
 * シルバーエッグにリクエストする際のパラメータ型
 */
interface RecommendParam {
  callbackName: string
  merch: string
  cat: string
  spec: string
  cookie: string
  num: number
  prod?: string
}

// ---------------------------------------------------------------
// 関数

export default class SilverEgg {
  // --------------------------------------------------------------------------
  // local prop
  private _recommendId = ''
  private _domain = ''
  private _specId: SpecId = {
    product: '',
    application: ''
  }

  /**
   * constructor
   *
   * @param domain シルバーエッグのドメイン
   * @param specId ページのSpecId
   */
  constructor(domain: string, specId: SpecId) {
    this._domain = domain
    this._specId = specId
  }

  // --------------------------------------------------------------------------
  // レコメンド用の固有ID取得

  /**
   * シルバーエッグレコメンド用の固有IDを問い合わせる
   *
   * @return 固有IDが取得できたかの可否
   */
  public async getRecommendID(): Promise<boolean> {
    if (!this.checkAccept()) {
      // プライバシーポリシー・シルバーエッグに未同意：エラー
      this._recommendId = 'error'
      return false
    }

    const cookie = new Cookie()
    const cookieName = 'RECOMMENDID'

    // クッキー情報取得
    if (cookie.hasKey(cookieName)) {
      // cookieが存在する場合、cookieからIDを取得
      // さらにクッキーの有効期限を60日(60*60*24*60秒)に再設定
      this._recommendId = cookie.get(cookieName)
      cookie.setCookie(cookieName, this._recommendId, 5184000, '/')
    } else {
      // cookieが存在しない場合、サーバーにリクエスト
      this.fetchRecommendId()
    }

    return true
  }

  /**
   * プライバシーポリシー・シルバーエッグに同意しているか判定
   *
   * オプトアウト方式なので、Cookieに[silver_accept]が無ければtrueとなる
   *
   * @return 同意の可否
   */
  private checkAccept(): boolean {
    const cookie = new GetCookie()
    return !cookie.hasKey('silver_accept')
  }

  /**
   * レコメンドIDを取得するために問い合わせる
   *
   * サーバ側でCookieをセットするので、問い合わせを行うのみ
   */
  private async fetchRecommendId(): Promise<void> {
    try {
      const res = await axios.get(API_PATH_REQUEST_ID)
      if (res.data === '' || res.data === null) {
        throw new Error('is_null')
      }
      this._recommendId = res.data
    } catch (e) {
      let status = 200
      if (axios.isAxiosError(e)) {
        status = e.response?.status as number
      }

      this._recommendId = 'error'
      const param = `kind=SE&err=reqID&status=${status}&details=cookieID_is_null`
      this.requestError(param)
    }
  }

  // --------------------------------------------------------------------------
  // レコメンド取得

  /**
   * シルバーエッグにレコメンドを問い合わせる
   *
   * @param recommendType 呼び出し元の種別
   * @param type 取得したいレコメンドの種別
   * @param nid 呼び出し元のページのnid
   * @returns レコメンド情報の配列
   */
  public async fetchRecommend(
    recommendType: RecommendType,
    type: SpecType,
    nid?: number
  ): Promise<RecommendItem[]> {
    const callbackName =
      type === 'application'
        ? 'getApplicationRecommendJson'
        : 'getProductRecommendJson'

    // 取得したいレコメンドのカテゴリを判別
    const cat = type === 'application' ? 'application_note' : 'pc-616'

    // エラー発生時のページ判別コード
    const page = PAGES[recommendType][type]

    // アクセス用URL取得
    const protocol = window.location.protocol
    const recommendUrl = `${protocol}//${this._domain}${REQ_PATH_RECOMMEND}`

    // API実行
    try {
      const param: RecommendParam = {
        callbackName: callbackName,
        merch: MERCH_ID,
        cat: cat,
        spec: this._specId[type],
        cookie: this._recommendId,
        num: 3
      }

      // 製品のReccomend情報を取得するときだけ、
      // 現在のページのnidを指定する
      if (type === 'product' && nid) {
        param.prod = 'p-' + nid
      }

      const json = await jsonp(recommendUrl, param, 10000)

      // null check
      const keys = Object.keys(json)
      if (keys.length === 0) {
        this.requestError(this.makeErrorParam(ErrorCode.JSONNULL, page))
        return []
      }

      // format check
      if (!check(json)) {
        this.requestError(this.makeErrorParam(ErrorCode.JSONFORMAT, page))
        return []
      }

      // パラメータチェック＆使いやすいように加工
      const contents = RecommendResponse.crateRecommendResponse(json)
      if (contents.paramCheckResult !== '') {
        const msg = ErrorCode.NONEXISTPARAM + contents.paramCheckResult
        this.requestError(this.makeErrorParam(msg, page))
        return []
      }

      // success
      return contents.recommendItems
    } catch (e) {
      // 通信失敗
      if ((e as Response).status !== undefined) {
        this.requestError(
          this.makeErrorParam(ErrorCode.SENDERROR, page, (e as Response).status)
        )
      }
    }

    return []
  }

  /**
   * エラーリクエストの際のパラメータを作成する
   * @param code エラー詳細情報コード
   * @param page 発生ページ
   * @param status ステータスコード
   * @return パラメータ
   */
  private makeErrorParam(code: string, page: string, status = 200): string {
    return `kind=SE&err=displayrec&status=${status}&page=${page}&details=${code}`
  }

  // --------------------------------------------------------------------------
  // GTMへのイベント送信

  /**
   * GTMへレコメンドクリックイベントの送信
   */
  public async sendLinkClickEventToGtm(
    item: RecommendItem,
    recommendType: RecommendType,
    type: SpecType
  ): Promise<void> {
    // cookie-id の払い出し
    await this.getRecommendID()
    if (this._recommendId === 'error') {
      return
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'RecommendLinkClickEvent',
      'recommend-merchant-id': MERCH_ID,
      'recommend-item-id': item.id,
      'recommend-spec-id': item.spec,
      'recommend-cookie-id': this._recommendId,
      'recommend-cref': item.rquid,
      'recommend-link-url': item.ctUrl,
      'recommend-link-id': item.num,
      'recommend-type': `${recommendType}_${type}`
    })
  }

  /**
   * GTMへページアクセスイベントの送信
   */
  public async sendPageAccessEventToGtm(): Promise<void> {
    await this.getRecommendID()
    if (this._recommendId === 'error') {
      return
    }

    const data: GtmPost = {
      event: 'RecommendPageAccessEvent',
      'recommend-merchant-id': MERCH_ID,
      'recommend-spec-id': this._specId.product,
      'recommend-cookie-id': this._recommendId
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
  }

  /**
   * GTMへPDFダウンロードイベントの送信
   *
   * @param docUrl ダウンロード対象PDFのURL(ダウンロード処理にてPIMに存在確認済み)
   */
  public async sendDocumentDlEventToGtm(docUrl: string): Promise<void> {
    // URLからPDFのpimDocumentFileエンティティのIDを取得し、item-id とする
    const urlElems = docUrl.split('/')
    const itemId = Number(urlElems[urlElems.length - 2])

    // item-idが正しく取得できなかった場合、終了
    if (isNaN(itemId) || itemId === 0) {
      this.requestError(
        '?kind=SE&err=sendinfo&status=0&details=pdf_error_get_item_id'
      )
      return
    }

    // GTMへイベント送信
    await this.getRecommendID()
    if (this._recommendId !== 'error') {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'RecommendDocumentDlEvent',
        'recommend-merchant-id': MERCH_ID,
        'recommend-item-id': `d-${itemId}`,
        'recommend-spec-id': 'pc312',
        'recommend-cookie-id': this._recommendId
      })
    }
  }

  /**
   * error.htmlにリクエスト通信を行う
   * @param param パラメータ
   */
  private requestError = (param: string): void => {
    // エラー通知リクエスト
    axios.get(PathUtil.makePath(`/error.html?${param}`))
  }
}
